import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiMaximize2, FiX } = FiIcons;

const Partys = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeFlyer, setActiveFlyer] = useState("");

  // Flyer URLs
  const jahrespartyFlyer = "https://grafiken.trust-franken.de/HP/Party2026.jpg";
  const italienAbendFlyer = "https://grafiken.trust-franken.de/HP/FrankenFIA.jpg"; 
  
  const headerImage = "https://grafiken.trust-franken.de/HP/2150704693.jpg";
  const logoUrl = "https://grafiken.trust-franken.de/HP/Kreuz.png";

  const events = [
    {
      date: "10.07.",
      year: "2026",
      title: "Fränkisch - Italienischer Abend",
      desc: "Spezialitäten aus Franken und Italien. Ein kulinarischer Abend im Clubhaus.",
      flyer: italienAbendFlyer,
      type: "Themenabend",
      hasFlyer: true
    },
    {
      date: "11.07.",
      year: "2026",
      title: "Jahresparty 2026",
      desc: "Die legendäre Jahresparty des Trust MC Franken.",
      flyer: jahrespartyFlyer,
      type: "Großveranstaltung",
      hasFlyer: true
    }
  ];

  const handleExpand = (flyer) => {
    setActiveFlyer(flyer);
    setIsExpanded(true);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Header Section */}
      <section className="relative h-[60vh] flex items-end overflow-hidden">
        <motion.img 
          initial={{ scale: 1.5 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2 }}
          src={headerImage} 
          alt="Partys Header" 
          className="absolute inset-0 w-full h-full object-cover opacity-50" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-16">
          <div className="flex flex-col-reverse md:flex-row items-center md:items-end justify-between gap-10">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-center md:text-left"
            >
              <h1 className="text-6xl md:text-9xl font-black uppercase tracking-tighter leading-none">
                Events
              </h1>
              <div className="h-2 bg-white w-24 mx-auto md:mx-0 mt-4" />
            </motion.div>
            <motion.img 
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              src={logoUrl} 
              alt="Trust MC Logo" 
              className="h-40 md:h-64 w-auto drop-shadow-[0_0_40px_rgba(255,255,255,0.2)]"
            />
          </div>
        </div>
      </section>

      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Featured Event - Nächstes Event */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-40 items-center">
            <div className="lg:col-span-8 flex flex-col justify-center">
              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="mb-8"
              >
                <span className="inline-block px-4 py-1 bg-white text-black text-xs font-black uppercase tracking-widest mb-4">
                  Nächstes Event
                </span>
                <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-6">
                   {events[0].title}
                </h2>
                <div className="flex items-center gap-4 text-2xl font-bold text-gray-400 mb-8">
                  <span className="text-white">{events[0].date}{events[0].year}</span>
                  <span className="w-2 h-2 bg-gray-700 rounded-full" />
                  <span>Clubhaus</span>
                </div>
                <p className="text-xl text-gray-400 leading-relaxed max-w-2xl mb-10">
                  {events[0].desc}
                </p>
              </motion.div>
            </div>
            
            <div className="lg:col-span-4 flex justify-center lg:justify-end">
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                className="relative cursor-pointer group rounded-2xl overflow-hidden shadow-2xl border border-white/10 w-48 md:w-64"
                onClick={() => handleExpand(events[0].flyer)}
              >
                <img 
                  src={events[0].flyer} 
                  alt="Flyer Mini" 
                  className="w-full h-auto transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                   <div className="bg-white text-black p-4 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-500">
                     <SafeIcon icon={FiMaximize2} className="text-xl" />
                   </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* All Events List */}
          <div className="space-y-12">
            <h3 className="text-3xl font-black uppercase tracking-tight flex items-center gap-4 mb-12">
              <SafeIcon icon={FiCalendar} />
              Termine 2026
            </h3>
            
            <div className="grid gap-6">
              {events.map((event, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={event.hasFlyer ? { x: 15, backgroundColor: 'rgba(255,255,255,0.03)' } : {}}
                  onClick={() => event.hasFlyer && handleExpand(event.flyer)}
                  className={`flex flex-col md:flex-row items-center gap-8 p-8 md:p-12 bg-gray-900/20 border border-white/5 rounded-[2.5rem] transition-all ${event.hasFlyer ? 'cursor-pointer group' : 'cursor-default'}`}
                >
                  <div className="text-center min-w-[120px]">
                    <span className="block text-5xl font-black tracking-tighter">{event.date}</span>
                    <span className="text-gray-500 font-bold uppercase tracking-widest text-sm">{event.year}</span>
                  </div>
                  
                  <div className="flex-grow">
                    <span className="text-xs font-black text-gray-500 uppercase tracking-widest mb-2 block">
                      {event.type}
                    </span>
                    <h4 className="text-3xl font-black uppercase mb-2 group-hover:text-white transition-colors">
                      {event.title}
                    </h4>
                    <p className="text-gray-400 font-light">{event.desc}</p>
                  </div>

                  {event.hasFlyer && (
                    <div className="flex flex-col items-end gap-3">
                      <div className="px-4 py-2 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest group-hover:bg-white group-hover:text-black transition-all">
                        Flyer öffnen
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsExpanded(false)}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-3xl flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          >
            <motion.button 
              whileHover={{ rotate: 90, scale: 1.2 }}
              className="absolute top-8 right-8 text-white text-5xl z-[110]"
              onClick={() => setIsExpanded(false)}
            >
              <SafeIcon icon={FiX} />
            </motion.button>
            
            <motion.div
              initial={{ scale: 0.8, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.8, y: 50, opacity: 0 }}
              className="relative max-w-4xl w-full h-full flex items-center justify-center p-4"
            >
              <img 
                src={activeFlyer} 
                alt="Event Flyer Full" 
                className="max-w-full max-h-full object-contain rounded-2xl shadow-[0_0_80px_rgba(255,255,255,0.15)] border border-white/10" 
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Partys;